// the shell: sidebar, rail, flyouts, top bar.
import { $, $$, closeLayer, csrf, esc, narrow, openLayer, play } from './core.ts';
import { normQ } from './select.ts';
import { toast } from './toast.ts';

/** The rail's open flyout and the menu button it belongs to. */
interface Flyout {
  el: HTMLDivElement;
  btn: HTMLElement;
}

// ---------------- shell ----------------
export function initShell(): void {
  const app = $('.ds-app');
  if (!app) return;
  const toggle = $('[data-ds-rail-toggle]');
  const syncRail = (): void => {
    const c = app.classList.contains('ds-rail');
    toggle?.setAttribute('aria-expanded', String(!c));
    toggle?.setAttribute('aria-label', c ? 'باز کردن منو' : 'جمع کردن منو');
  };
  toggle?.addEventListener('click', () => {
    app.classList.toggle('ds-rail');
    syncRail();
    closeFlyout();
    document.cookie =
      'ds-rail=' + (app.classList.contains('ds-rail') ? '1' : '0') + ';path=/;max-age=31536000;samesite=lax';
    $$('.ds-brand .ds-mark').forEach(play);
  });
  syncRail();
  // drawer
  const scrim = $('.ds-scrim');
  const setDrawer = (open: boolean): void => {
    app.classList.toggle('ds-drawer-open', open);
    if (scrim) scrim.hidden = !open;
    $$('[data-ds-drawer-open]').forEach(b => b.setAttribute('aria-expanded', String(open)));
    if (open) {
      $('[data-ds-drawer-close]')?.focus();
      openLayer(closeDrawer, false);
    } else closeLayer(closeDrawer);
  };
  const closeDrawer = (): void => setDrawer(false);
  $$('[data-ds-drawer-open]').forEach(b =>
    b.addEventListener('click', e => {
      e.preventDefault();
      setDrawer(true);
    }),
  );
  $$('[data-ds-drawer-close]').forEach(b =>
    b.addEventListener('click', () => {
      setDrawer(false);
      $('[data-ds-drawer-open]')?.focus();
    }),
  );
  scrim?.addEventListener('click', closeDrawer);
  // accordion + rail flyout
  $$('.ds-nav').forEach(wireMenu);
  // top bar popovers
  $$('[data-ds-pop]').forEach(btn => {
    const pop = document.getElementById(btn.dataset.dsPop!);
    if (!pop) return;
    const home = pop.parentElement!;
    const close = (): void => {
      pop.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
      closeLayer(close);
    };
    btn.addEventListener('click', e => {
      e.stopPropagation();
      if (!pop.hidden) {
        close();
        return;
      }
      // phone: a bottom sheet. It must live outside the top bar, whose backdrop-filter would otherwise be the
      // containing block of its position:fixed (the sheet opened above the screen) and cap its layer (review 2026-10-06).
      const target = narrow(640) ? document.body : home;
      if (pop.parentElement !== target) target.appendChild(pop);
      openLayer(close);
      pop.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      $$('.ds-ico', pop).forEach(play);
      pop.querySelector<HTMLElement>('button,a,[tabindex]')?.focus();
    });
    document.addEventListener(
      'pointerdown',
      e => {
        if (!pop.hidden && !pop.contains(e.target as Node) && !btn.contains(e.target as Node)) close();
      },
      true,
    );
  });
  // top-bar search: finds pages in the user's own menu; Enter opens the first match
  const search = $('[data-ds-menu-search]');
  if (search) {
    const input = $<HTMLInputElement>('input', search)!,
      links = $$<HTMLAnchorElement>('#nav-tree a[href]');
    const list = document.createElement('div');
    list.className = 'ds-pop';
    list.style.width = '320px';
    list.hidden = true;
    list.setAttribute('role', 'listbox');
    search.style.position = 'relative';
    search.appendChild(list);
    const close = (): void => {
      list.hidden = true;
      closeLayer(close);
    };
    const matches = (): HTMLAnchorElement[] => {
      const q = normQ(input.value.trim());
      return q ? links.filter(a => normQ(a.textContent).includes(q)).slice(0, 8) : [];
    };
    input.addEventListener('input', () => {
      const m = matches();
      if (!m.length) {
        close();
        return;
      }
      list.innerHTML = m
        .map(
          a => `<a class="ds-mi" role="option" href="${esc(a.getAttribute('href'))}">${esc(a.textContent.trim())}</a>`,
        )
        .join('');
      if (list.hidden) {
        openLayer(close, false);
        list.hidden = false;
      }
    });
    search.addEventListener('submit', e => {
      e.preventDefault();
      const m = matches();
      if (m[0]) location.href = m[0].getAttribute('href')!;
    });
    document.addEventListener(
      'pointerdown',
      e => {
        if (!list.hidden && !search.contains(e.target as Node)) close();
      },
      true,
    );
  }
  // theme choice: kept by the app's theme store (the account, or this browser), applied at once
  $$('[data-ds-theme-set]').forEach(b =>
    b.addEventListener('click', async () => {
      const pref = b.dataset.dsThemeSet!;
      document.documentElement.setAttribute('data-ds-theme', pref);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- theme-boot may be missing on a page
      window.dsApplyTheme?.();
      $$('[data-ds-theme-set]').forEach(x => x.setAttribute('aria-pressed', String(x.dataset.dsThemeSet === pref)));
      try {
        // T-142: the app's endpoint (Shell:ThemeSaveUrl) or the platform's, written by the account menu
        const url = b.closest<HTMLElement>('[data-ds-theme-url]')?.dataset.dsThemeUrl ?? '/_shell/theme';
        const r = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded', RequestVerificationToken: csrf() },
          body: 'theme=' + pref,
          credentials: 'same-origin',
        });
        // the message is only the status code (never shown; the catch below answers)
        if (!r.ok) throw new Error(r.status as unknown as string);
      } catch {
        toast('حالت نمایش روی این دستگاه عوض شد ولی ذخیره نشد؛ دوباره امتحان کنید.', { type: 'warning' });
      }
    }),
  );
}

let fly: Flyout | null = null;
function closeFlyout(): void {
  if (!fly) return;
  fly.btn.setAttribute('aria-expanded', String(fly.btn.classList.contains('on-path')));
  fly.el.remove();
  closeLayer(closeFlyout);
  fly = null;
}
function wireMenu(root: HTMLElement): void {
  root.addEventListener('click', e => {
    const b = (e.target as Element).closest<HTMLElement>('.ds-np');
    if (!b || !root.contains(b)) return;
    const app = $('.ds-app');
    if (app?.classList.contains('ds-rail') && b.dataset.depth === '0' && root.closest('.ds-side') && !narrow(860)) {
      openFlyout(b);
      return;
    }
    const w = document.getElementById(b.getAttribute('aria-controls')!);
    if (!w) return;
    const open = b.getAttribute('aria-expanded') !== 'true';
    b.setAttribute('aria-expanded', String(open));
    w.classList.toggle('open', open);
  });
}
function openFlyout(b: HTMLElement): void {
  if (fly && fly.btn === b) {
    closeFlyout();
    return;
  }
  closeFlyout();
  const sub = document.getElementById(b.getAttribute('aria-controls')!)?.firstElementChild;
  if (!sub) return;
  const el = document.createElement('div');
  el.className = 'ds-flyout ds-nav';
  el.setAttribute('role', 'group');
  el.setAttribute('aria-label', b.dataset.tip || '');
  el.innerHTML = `<h6>${b.querySelector('.ds-ico')?.outerHTML || ''}${esc(b.dataset.tip)}</h6>`;
  const clone = sub.cloneNode(true) as Element;
  $$('[id]', clone).forEach(n => {
    n.id += '-fly';
  });
  $$('[aria-controls]', clone).forEach(n => n.setAttribute('aria-controls', n.getAttribute('aria-controls')! + '-fly'));
  el.appendChild(clone);
  document.body.appendChild(el);
  const r = b.getBoundingClientRect();
  el.style.right = innerWidth - r.left + 8 + 'px';
  el.style.top = Math.max(8, Math.min(r.top - 8, innerHeight - el.offsetHeight - 8)) + 'px';
  wireMenu(el);
  b.setAttribute('aria-expanded', 'true');
  fly = { el, btn: b };
  openLayer(closeFlyout);
  el.querySelector<HTMLElement>('a,.ds-np')?.focus();
  setTimeout(() =>
    document.addEventListener(
      'pointerdown',
      function out(e) {
        if (fly && !fly.el.contains(e.target as Node) && !fly.btn.contains(e.target as Node)) {
          closeFlyout();
          document.removeEventListener('pointerdown', out, true);
        }
      },
      true,
    ),
  );
}
